import { ref, computed } from 'vue';
import { StrategyFactory } from './strategies/StrategyFactory';
import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

export interface StrategyComparisonResult {
    strategyId: string;
    strategyName: string;
    result: ICutResult;
}


export function useOptimizer() {
    const selectedStrategyId = ref('debug-strategy-compare');
    const optimizationResult = ref<ICutResult | null>(null);
    const isOptimizing = ref(false);
    const error = ref<string | null>(null);
    const comparisonResults = ref<StrategyComparisonResult[]>([]);
    const isComparingStrategies = ref(false);

    const availableStrategies = computed(() => {
        return StrategyFactory.getAvailableStrategies();
    });

    const currentStrategy = computed(() => {
        try {
            return StrategyFactory.getStrategy(selectedStrategyId.value);
        } catch (e: any) {
            error.value = e.message;
            return null;
        }
    });

    const optimize = async (material: IMaterial, parts: IMaterial[]): Promise<ICutResult | null> => {
        if (!currentStrategy.value) {
            error.value = 'No valid strategy selected';
            return null;
        }

        error.value = null;
        isOptimizing.value = true;

        try {
            // Using setTimeout to allow UI to update before potentially heavy computation
            return await new Promise(resolve => {
                setTimeout(() => {
                    const result = currentStrategy.value!.optimize(material, parts);
                    optimizationResult.value = result;
                    isOptimizing.value = false;
                    resolve(result);
                }, 0);
            });
        } catch (e: any) {
            error.value = e.message;
            isOptimizing.value = false;
            return null;
        }
    };

    const compareAllStrategies = async (material: IMaterial, parts: IMaterial[]): Promise<StrategyComparisonResult[]> => {
        error.value = null;
        isComparingStrategies.value = true;
        comparisonResults.value = [];

        try {
            const strategies = availableStrategies.value.filter(s => s.id !== 'orientation-compare');
            const results: StrategyComparisonResult[] = [];

            // Run each strategy sequentially
            for (const strategy of strategies) {
                try {
                    const strategyInstance = StrategyFactory.getStrategy(strategy.id);
                    const result = strategyInstance.optimize(material, parts);

                    results.push({
                        strategyId: strategy.id,
                        strategyName: strategy.name,
                        result
                    });
                } catch (e: any) {
                    console.error(`Error running strategy ${strategy.id}:`, e);
                }
            }

            // Sort results by waste percentage (ascending)
            results.sort((a, b) => a.result.wastePercentage - b.result.wastePercentage);

            comparisonResults.value = results;

            // Set the best strategy as the current one and update the result
            if (results.length > 0) {
                selectedStrategyId.value = results[0].strategyId;
                optimizationResult.value = results[0].result;
            }

            isComparingStrategies.value = false;
            return results;
        } catch (e: any) {
            error.value = e.message;
            isComparingStrategies.value = false;
            return [];
        }
    };

    return {
        selectedStrategyId,
        availableStrategies,
        currentStrategy,
        optimizationResult,
        isOptimizing,
        error,
        optimize,
        compareAllStrategies,
        comparisonResults,
        isComparingStrategies
    };
}
